import {
  faBookOpen,
  faBooks,
  faBuilding,
  faCircleNodes,
  faClipboardCheck,
  faClockRotateLeft,
  faCubes,
  faFlaskVial,
  faHome,
  faInbox,
  faKey,
  faLayerGroup,
  faLock,
  faRobot,
  faShieldCheck,
  faSignOut,
  faSitemap,
  faTableList,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { useAuth } from "@intility/authentication-react";
import { Badge, Nav } from "@intility/bifrost-react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useCapabilities, useIsProviderAdmin } from "../../auth/capabilities";
import { auth } from "../../auth/index";
import { isFromMyHub } from "../../lib/fromMyHub";
import { ColorModePicker } from "./ColorMode";

const isProduction = import.meta.env.VITE_ENVIRONMENT === "production";

export default function RootLayout() {
  const { authenticated } = useAuth();
  const { can } = useCapabilities();
  const { isProviderAdmin } = useIsProviderAdmin();
  const location = useLocation();
  // The platform page was reached from My Hub (the card passes that in the
  // router state), so the menu should still show My Hub as the place.
  const openedFromMyHub =
    isFromMyHub(location.state) && location.pathname.startsWith("/platforms/");

  const userName =
    auth.keycloak.tokenParsed?.name ??
    auth.keycloak.tokenParsed?.preferred_username ??
    "Developer";

  return (
    <Nav
      logo={
        <Link
          className="bf-neutral-link"
          to="/"
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <Nav.Logo>Intility ID</Nav.Logo>
          <Badge state={isProduction ? "success" : "warning"} pill>
            {isProduction ? "Production" : "Development"}
          </Badge>
        </Link>
      }
      side={
        <>
          <NavLink to="/">
            <Nav.Item icon={faHome}>Home</Nav.Item>
          </NavLink>
          {/* A platform opened from My Hub keeps My Hub selected in the menu,
              and Platforms stays unselected. */}
          <NavLink
            to="/myhub"
            className={({ isActive }) =>
              isActive || openedFromMyHub ? "active" : ""
            }
          >
            <Nav.Item icon={faCircleNodes}>My Hub</Nav.Item>
          </NavLink>

          <Nav.Header>Identity</Nav.Header>
          {can("platforms.read") && (
            <NavLink
              to="/platforms"
              className={({ isActive }) =>
                isActive && !openedFromMyHub ? "active" : ""
              }
            >
              <Nav.Item icon={faLayerGroup}>Platforms</Nav.Item>
            </NavLink>
          )}
          {can("platforms.read") && (
            <NavLink to="/clients">
              <Nav.Item icon={faRobot}>Clients</Nav.Item>
            </NavLink>
          )}
          {can("spiffe.manage") && (
            <NavLink to="/spiffe-clusters">
              <Nav.Item icon={faShieldCheck}>SPIFFE Clusters</Nav.Item>
            </NavLink>
          )}

          <Nav.Header>Authorization</Nav.Header>
          <NavLink to="/blueprint-resources">
            <Nav.Item icon={faBooks}>Blueprint resources</Nav.Item>
          </NavLink>

          {can("authorization.read") && (
            <Nav.Group
              icon={faTableList}
              name="Configuration"
              defaultExpanded={false}
            >
              <NavLink to="/authorization/check">
                <Nav.Item icon={faClipboardCheck}>Check</Nav.Item>
              </NavLink>
              <NavLink to="/authorization/tuples">
                <Nav.Item icon={faTableList}>Tuples</Nav.Item>
              </NavLink>
              <NavLink to="/authorization/model">
                <Nav.Item icon={faCubes}>Model</Nav.Item>
              </NavLink>
              <NavLink to="/authorization/tests">
                <Nav.Item icon={faFlaskVial}>Tests</Nav.Item>
              </NavLink>
            </Nav.Group>
          )}

          {/* Self-scoping (ADR-0033): every caller sees exactly the events within their own
              authority scope — no capability gate, an empty scope just renders an empty feed. */}
          <NavLink to="/audit-log">
            <Nav.Item icon={faClockRotateLeft}>Audit Log</Nav.Item>
          </NavLink>

          {can("governance.read") && (
            <Nav.Group icon={faLock} name="Management" defaultExpanded={false}>
              <NavLink to="/management">
                <Nav.Item icon={faTableList}>Tuples</Nav.Item>
              </NavLink>
              <NavLink to="/management/model">
                <Nav.Item icon={faCubes}>Model</Nav.Item>
              </NavLink>
              <NavLink to="/management/access-tree">
                <Nav.Item icon={faSitemap}>Access Tree</Nav.Item>
              </NavLink>
              <NavLink to="/management/audit-log">
                <Nav.Item icon={faClockRotateLeft}>Audit Log</Nav.Item>
              </NavLink>
            </Nav.Group>
          )}

          <div
            style={{
              borderTop: "1px solid var(--bfc-base-c-dimmed)",
              marginTop: 8,
              paddingTop: 8,
            }}
          />

          {can("requests.read") && (
            <NavLink to="/requests">
              <Nav.Item icon={faInbox}>Requests</Nav.Item>
            </NavLink>
          )}

          <Nav.Group
            icon={faBookOpen}
            name="Documentation"
            defaultExpanded={false}
          >
            <NavLink to="/docs/concepts">
              <Nav.Item>Concepts</Nav.Item>
            </NavLink>
            <NavLink to="/docs/getting-started">
              <Nav.Item>Getting started</Nav.Item>
            </NavLink>
            <NavLink to="/docs/identity">
              <Nav.Item>Identity & auth</Nav.Item>
            </NavLink>
            <NavLink to="/docs/authorization">
              <Nav.Item>Authorization</Nav.Item>
            </NavLink>
            <NavLink to="/docs/workload-identity">
              <Nav.Item>Workload identity</Nav.Item>
            </NavLink>
            <NavLink to="/docs/reference">
              <Nav.Item>Reference</Nav.Item>
            </NavLink>
          </Nav.Group>

          {/* Last in the sidebar: only provider admins see it. */}
          {isProviderAdmin && (
            <NavLink to="/companies">
              <Nav.Item icon={faBuilding}>Companies</Nav.Item>
            </NavLink>
          )}
        </>
      }
      top={
        <>
          {/* Self-scoping (ADR-0033): fga-api returns only what the caller can see —
              authentication is the only gate, same as /audit-log. */}
          <NavLink to="/access-tree">
            <Nav.Item icon={faSitemap}>Access Tree</Nav.Item>
          </NavLink>
          {/* Self-scoping like /access-tree: the page lists only what the caller
              holds admin on, and fga-api enforces every write. */}
          <NavLink to="/grants">
            <Nav.Item icon={faKey}>Grants</Nav.Item>
          </NavLink>
          <Nav.Item icon={faUser}>{userName}</Nav.Item>
          {authenticated && (
            <Nav.Item
              icon={faSignOut}
              onClick={() =>
                auth.keycloak.logout({ redirectUri: window.location.origin })
              }
            >
              Logout
            </Nav.Item>
          )}
          <ColorModePicker />
        </>
      }
    >
      <Outlet />
    </Nav>
  );
}
